import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { triggerInstallPrompt } from '../utils/installPromptUtils';
import { getToolUsageCount, TOOL_USAGE_UPDATED_EVENT } from '../utils/toolUsageUtils';
import './InstallButton.css';

// Shown as soon as the browser says the app can be installed (it is never shown where it cannot be).
const MIN_TOOLS_USED = 0;

interface InstallButtonProps {
  promptReady: boolean;
  onInstalled: () => void;
}

export function InstallButton({ promptReady, onInstalled }: InstallButtonProps) {
  const [showButton, setShowButton] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Monitor tool usage count
  useEffect(() => {
    const updateVisibility = () => {
      if (promptReady && getToolUsageCount() >= MIN_TOOLS_USED) {
        setShowButton(true);
      }
    };

    updateVisibility();
    window.addEventListener(TOOL_USAGE_UPDATED_EVENT, updateVisibility);
    return () => window.removeEventListener(TOOL_USAGE_UPDATED_EVENT, updateVisibility);
  }, [promptReady]);

  const handleInstall = async () => {
    setIsInstalling(true);
    const success = await triggerInstallPrompt();
    setIsInstalling(false);

    if (success) {
      setShowButton(false);
      onInstalled();
    }
  };

  if (!showButton) return null;

  return (
    <button
      className="install-button"
      onClick={handleInstall}
      disabled={isInstalling}
      aria-label="Install DevStackKit as an app"
      title="Install app for offline access and home screen shortcut"
    >
      <Download size={16} />
      <span>{isInstalling ? 'Installing…' : 'Install'}</span>
    </button>
  );
}
